import * as React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import { IntakeConsent } from '@app/components/intake/consent'
import { IntakeDemographics } from '@app/components/intake/demographics'
import { IntakeForm } from '@app/components/intake/form'
import { IntakeInformation } from '@app/components/intake/information'
import { IntakeCompleteModal } from '@app/components/intake/intake-complete-modal'
import { IntakeResumeModal } from '@app/components/intake/intake-resume-modal'
import { Page, PageError, PageLoading } from '@app/components/intake/page'
import { IntakeSignature } from '@app/components/intake/signature'
import { SurveyCompleteModal } from '@app/components/intake/survey/survey-complete-modal'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import { Role } from '@app/domain/iam/user.interface'
import type { Question } from '@app/domain/instrument'
import type { IEntry } from '@app/domain/instrument/instrument.interface'
import type { IPatient } from '@app/domain/patient/patient.interface'
import type {
  IDataInstrument,
  IScreen,
} from '@app/domain/visit/visit.interface'
import {
  IWorkFlowStepType,
  VisitStatusId,
} from '@app/domain/visit/visit.interface'
import type { RootState } from '@app/state'
import * as Auth from '@app/state/authenticate'
import * as Intake from '@app/state/intake'
import { visitHasResponses } from '@app/state/intake/helpers'
import type { IVisitState } from '@app/state/intake/state'
import { VisitStage } from '@app/state/intake/state'
import * as User from '@app/state/user'
import { ActionStatusId } from '@app/state/visits/state'

import type { ConnectedProps } from 'react-redux'
import type { RouteComponentProps } from 'react-router-dom'

const mapState = (state: RootState) => ({
  apiConfig: state.config.api,
  visit: state.intake.visit,
  visitStatusOnLoad: state.intake.visitStatusOnLoad,
  definition: state.intake.definition,
  visitState: state.intake.currentState,
  entries: state.intake.currentState.entries,
  status: state.intake.loadStatus,
  verifyPinStatus: state.user.verifyPinStatus,
  witnessStatus: state.intake.witnessStatus,
  featureFlags: state.featureFlags,
})

const mapDispatch = (dispatch) => {
  return {
    clearContext: () => dispatch(Intake.actions.clearContext()),
    continue: () => dispatch(Intake.actions.continue()),
    authenticateResponses: (patient: IPatient, signature: string) =>
      dispatch(Intake.actions.authenticateResponses(patient, signature)),
    goBack: () => dispatch(Intake.actions.goBack()),
    loadVisit: (id: string) => dispatch(Intake.actions.loadVisit(id)),
    restart: () => dispatch(Intake.actions.restart()),
    submit: (
      entryId: string,
      responses: Record<string, string>,
      preventNextScreen?: boolean,
    ) =>
      dispatch(
        Intake.actions.submitResponses(
          entryId,
          responses,
          preventNextScreen ?? false,
        ),
      ),
    evaluateDisplayLogic: (
      instrumentId: string,
      questionId?: string,
      response?: string,
    ) =>
      dispatch(
        Intake.actions.evaluateDisplayLogic(instrumentId, questionId, response),
      ),
    verifyPIN: (pin: string, route: string, closeSurvey?: boolean) =>
      dispatch(User.actions.verifyPIN(pin, route, closeSurvey)),
    completeVisit: (pin: string, signature: string) => {
      dispatch(Auth.actions.assumeRole(Role.Admin))
      dispatch(Intake.actions.witnessResponses(pin, signature))
    },
    closeSurvey: (route?: string) => {
      dispatch(Auth.actions.assumeRole(Role.Admin))
      dispatch(Intake.actions.closeSurvey(route))
    },
    setResumeStartPoint: (restartFromBeginning: boolean) => {
      dispatch(Intake.actions.setRestartFromBeginning(restartFromBeginning))
    },
    resume: (state: IVisitState) => dispatch(Intake.actions.resume(state)),
  }
}

const connector = connect(mapState, mapDispatch)
type PropsFromRedux = ConnectedProps<typeof connector>

type RouteParams = { id: string }
type Props = PropsFromRedux & RouteComponentProps<RouteParams>

interface ScreenState {
  validPIN?: boolean
  validWitness?: boolean
  isIntakeResumePointSet: boolean
}

type ConnectedScreenProps = Props

class IntakeVisitScreen extends React.Component<
  ConnectedScreenProps,
  ScreenState
> {
  /**
   * This is used to determine if we have changed screens during the overall survey.  If so, the
   * scroll position is reset to the top of the page in the componentDidUpdate method.  This is not
   * stored as state since it would trigger a re-render when updating, potentially causing the
   * scroll reset to be interrupted.  Using proper routing that describes the difference between
   * screen states would be the best solution as React Router would handle the scroll position
   * automatically in its route change lifecycle.
   */
  workflowStepKey: string

  constructor(props) {
    super(props)
    this.workflowStepKey = ''
    this.state = {
      validWitness: undefined,
      isIntakeResumePointSet: false,
    }
  }

  componentDidMount() {
    const { id } = this.props.match.params
    if (id) this.props.loadVisit(id)
  }

  componentWillUnmount() {
    this.props.clearContext()
  }

  componentDidUpdate(prevProps) {
    const { definition, status, visitState, verifyPinStatus, witnessStatus } =
      this.props
    if (
      (prevProps.status !== status && status === 'succeeded') ||
      prevProps.visitState.workflowStep !== visitState.workflowStep
    ) {
      const instrumentId =
        definition.allWorkflow[visitState.workflowStep]?.instrumentId
      const instrument = definition.allInstruments.find(
        (it) => it.id === instrumentId,
      )
      if (instrument) {
        this.props.evaluateDisplayLogic(instrument.id)
      }
    }

    if (prevProps.verifyPinStatus !== verifyPinStatus) {
      this.determinePINStatus(verifyPinStatus)
    }
    if (prevProps.witnessStatus !== witnessStatus) {
      this.determineWitnessStatus(witnessStatus)
    }

    const currentWorkflowStepKey = `${visitState.stage}-${visitState.workflowStep}-${visitState.screen}`
    if (this.workflowStepKey !== currentWorkflowStepKey) {
      const scrollParent = document.querySelector('.page-background')
      if (scrollParent) {
        scrollParent.scrollTo({ top: 0 })
      }
      this.workflowStepKey = currentWorkflowStepKey
    }
  }

  determineWitnessStatus = (status?: string) => {
    if (status !== undefined) {
      this.setState({
        validWitness: status === 'failure' ? false : true,
      })
    } else {
      this.setState({
        validWitness: undefined,
      })
    }
  }

  determinePINStatus = (verifyPINStatus?: string) => {
    if (verifyPINStatus !== undefined) {
      this.setState({
        validPIN: verifyPINStatus === 'failed' ? false : true,
      })
    } else {
      this.setState({
        validPIN: undefined,
      })
    }
  }

  getScreenQuestions = (instrument: IDataInstrument, screen: IScreen) => {
    if (screen && screen.questions) {
      return screen.questions
        .sort((a, b) => a.sortIndex - b.sortIndex)
        .map((screenQuestion) => {
          return {
            ...screenQuestion,
            question: instrument.questions[screenQuestion.questionId],
          }
        })
    } else {
      return []
    }
  }

  renderScreen = (
    instrument: IDataInstrument,
    entry: IEntry,
    screen: IScreen,
    questions: Question[],
  ) => {
    const handleGoBack = () => {
      if (
        this.props.visitState.workflowStep !== 0 ||
        this.props.visitState.screen !== 0
      ) {
        this.props.goBack()
      }
    }

    switch (instrument.type) {
      case 'form':
        return (
          <IntakeForm
            key={instrument.id}
            instrumentId={instrument.id}
            entry={entry}
            header={screen.header}
            content={screen.content}
            questions={questions}
            questionsConfig={this.props.apiConfig.questions}
            display={this.props.visitState.display}
            evaluateDisplayLogic={this.props.evaluateDisplayLogic}
            handleGoBack={handleGoBack}
            handleContinue={this.props.continue}
            handleSubmit={this.props.submit}
            visitStatus={this.props.visit.status}
          />
        )
      case 'consent':
        return (
          <IntakeConsent
            key={instrument.id}
            instrumentId={instrument.id}
            entry={entry}
            header={screen.header}
            content={screen.content}
            questions={questions}
            handleGoBack={handleGoBack}
            handleContinue={this.props.continue}
            handleSubmit={this.props.submit}
          />
        )
      case 'information':
        return (
          <IntakeInformation
            key={instrument.id}
            instrumentId={instrument.id}
            entry={entry}
            header={screen.header}
            content={screen.content}
            handleGoBack={handleGoBack}
            handleContinue={this.props.continue}
            handleSubmit={this.props.submit}
          />
        )
      default:
        return <h1>Unknown Instrument Type</h1>
    }
  }

  renderSignatureScreen = () => {
    return (
      <IntakeSignature
        handleContinue={this.props.authenticateResponses}
        patient={this.props.visit?.patient}
        header={`Please sign in the box below to confirm that you have answered the previous questions to the best of your ability.`}
      />
    )
  }
  renderCompleteVisitScreen = () => {
    const { featureFlags, visit } = this.props
    const handleVerifyPin = (pin: string) => {
      this.props.verifyPIN(pin, `/clinic/visit/${visit.id}`, true)
    }
    if (
      featureFlags[FeatureFlagId.BypassPatientSignature] ||
      !visit.definition.isWitnessSignatureRequired
    ) {
      return (
        <SurveyCompleteModal
          message={`You have reached the end of the survey!<br />If you would like to change any of your answers, please go back and do so. Otherwise, press the "Complete" button below.`}
          handleVerifyPin={handleVerifyPin}
          handleGoBack={this.props.goBack}
          hasValidWitness={this.state.validPIN}
        />
      )
    } else {
      return (
        <IntakeCompleteModal
          message={`Thank you for completing these forms.<br>Please return this device to the front desk.`}
          onComplete={this.props.completeVisit}
          hasValidWitness={this.state.validWitness}
        />
      )
    }
  }

  renderDemographicConfirmation(onExit: (pin: string) => void) {
    const handleGoBack = () => {
      if (
        this.props.visitState.workflowStep !== 0 ||
        this.props.visitState.screen !== 0
      ) {
        this.props.goBack()
      }
    }

    return (
      <Page onExit={onExit}>
        <IntakeDemographics
          patient={this.props.visit?.patient}
          handleContinue={this.props.continue}
          handleGoBack={handleGoBack}
          handleSubmit={this.props.submit}
        />
      </Page>
    )
  }

  renderForm(onExit: (pin: string) => void) {
    const { definition, visitState, entries } = this.props

    const instrumentId =
      definition.allWorkflow[visitState.workflowStep]?.instrumentId
    const instrument = definition.allInstruments.find(
      (it) => it.id === instrumentId,
    )

    if (instrument) {
      const views = instrument.views.filter((v) => v.useCase === 'intake')
      if (Array.isArray(views) && views.length) {
        const screen = views[0].screens[visitState.screen]
        const questions = this.getScreenQuestions(instrument, screen)
        const entry = entries[instrument.id]
        const progress = Math.round(
          (visitState.progress / definition.totalLength) * 100,
        )
        return (
          <Page
            onExit={onExit}
            progress={progress}
            instrument={instrument.label}
          >
            {this.renderScreen(
              instrument,
              entry,
              screen,
              questions.map((it) => it.question),
            )}
          </Page>
        )
      }
    }

    return <Page onExit={onExit}>Error Loading Visit</Page>
  }

  renderCollection(onExit: (pin: string) => void) {
    const { definition, visitState } = this.props
    const workflowIndex = visitState.workflowStep
    const workflowType = definition.allWorkflow[workflowIndex]?.type
    if (workflowType === IWorkFlowStepType.DemographicConfirmation) {
      return this.renderDemographicConfirmation(onExit)
    } else {
      return this.renderForm(onExit)
    }
  }

  render() {
    const { status, visit, visitState, entries } = this.props

    const onExit = (pin: string) =>
      this.props.verifyPIN(pin, `/clinic/visit/${visit.id}`)

    const handleResumeStartPoint = (resumeFromBeginning: boolean) => {
      this.props.setResumeStartPoint(resumeFromBeginning)
      this.setState({
        isIntakeResumePointSet: true,
      })
      if (resumeFromBeginning) {
        this.props.restart()
      } else {
        this.props.resume(this.props.visitState)
      }
    }

    const entriesArray = Object.keys(entries).reduce(
      (accumulator: IEntry[], key) => {
        const entry = entries[key]
        accumulator.push(entry)
        return accumulator
      },
      [],
    )

    if (status === ActionStatusId.Succeeded) {
      if (
        this.props.visitStatusOnLoad === VisitStatusId.InProgress &&
        visitHasResponses(entriesArray) &&
        !this.state.isIntakeResumePointSet
      ) {
        return (
          <Page onExit={onExit}>
            <IntakeResumeModal
              setResumeStartPoint={handleResumeStartPoint}
              patient={this.props.visit?.patient}
              handleContinue={this.props.continue}
              handleGoBack={this.props.goBack}
              handleSubmit={this.props.submit}
            />
          </Page>
        )
      }

      //this is a new visit or empty visit, we dont need to prompt
      if (
        !this.state.isIntakeResumePointSet &&
        !visitHasResponses(entriesArray)
      ) {
        this.setState({
          isIntakeResumePointSet: true,
        })
      }

      if (visitState.stage === VisitStage.Collection) {
        return this.renderCollection(onExit)
      } else if (visitState.stage === VisitStage.Signature) {
        return <Page onExit={onExit}>{this.renderSignatureScreen()}</Page>
      } else if (visitState.stage === VisitStage.Completed) {
        return <Page onExit={onExit}>{this.renderCompleteVisitScreen()}</Page>
      } else {
        return (
          <Page onExit={onExit}>
            <PageError title="Error in Visit">
              Please contact support if this issue persists.
            </PageError>
          </Page>
        )
      }
    } else if (status === ActionStatusId.Failed) {
      return (
        <Page onExit={onExit}>
          <PageError title="Error Loading Visit">
            You may try reloading this page. Please contact support if this
            issue persists.
          </PageError>
        </Page>
      )
    } else {
      return (
        <Page onExit={onExit}>
          <PageLoading>Loading...</PageLoading>
        </Page>
      )
    }
  }
}

export default withRouter(connector(IntakeVisitScreen))
